import React, { useEffect, useState } from 'react';
import { Sparkles, Gift, Play } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent } from '../ui/dialog';
import { useTour } from '../../tour/TourProvider';

// First-login welcome (full tour) or "What's new" prompt (returning users).
const TourWelcomeModal = () => {
  const tour = useTour();
  const [open, setOpen] = useState(false);
  const [dontShow, setDontShow] = useState(false);

  useEffect(() => {
    if (tour?.showWelcome) {
      const t = setTimeout(() => setOpen(true), 900);
      return () => clearTimeout(t);
    }
  }, [tour?.showWelcome]);

  if (!tour) return null;
  const whatsNew = tour.isWhatsNew;

  const handleStart = () => {
    setOpen(false);
    tour.startTour();
  };

  const handleSkip = () => {
    setOpen(false);
    if (whatsNew || dontShow) tour.dismissTour();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleSkip(); }}>
      <DialogContent
        className="max-w-md p-0 overflow-hidden border-0 shadow-2xl"
        data-testid="tour-welcome-modal"
      >
        <div className="bg-gradient-to-br from-amber-500 to-orange-600 px-6 py-8 text-center text-white">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/15 rounded-full mb-4">
            {whatsNew ? <Gift className="w-8 h-8" /> : <Sparkles className="w-8 h-8" />}
          </div>
          <h2 className="text-2xl font-bold mb-1" data-testid="tour-welcome-title">
            {whatsNew ? "What's new in the portal" : 'Welcome to MAD Donuts!'}
          </h2>
          <p className="text-amber-50/90 text-sm">
            {whatsNew
              ? 'A couple of new things have landed since your last visit. Take a quick look?'
              : "Let's take a 2-minute tour so you can order, pay, and manage your account with confidence."}
          </p>
        </div>

        <div className="px-6 py-6 space-y-5">
          {!whatsNew && (
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <Checkbox
                checked={dontShow}
                onCheckedChange={(v) => setDontShow(!!v)}
                data-testid="tour-dont-show-again"
              />
              Don't show this again
            </label>
          )}

          <div className="flex items-center justify-between gap-3">
            <Button
              variant="ghost"
              className="text-gray-500"
              onClick={handleSkip}
              data-testid="tour-skip-btn"
            >
              {whatsNew ? 'Dismiss' : 'Skip for now'}
            </Button>
            <Button
              className="bg-amber-500 hover:bg-amber-600 text-white"
              onClick={handleStart}
              data-testid="tour-start-btn"
            >
              <Play className="w-4 h-4 mr-2" />
              {whatsNew ? "Show me what's new" : 'Take the tour'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TourWelcomeModal;
